<script setup lang="ts">
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import FloatLabel from '@shared/form-v2/FloatLabel/FloatLabel.vue';
import VSelect from '@shared/form/VSelect/VSelect.vue';
import { computed, watch } from 'vue';
import { usePakketten } from '../../composables/pakketten';
import { useProlongatieHistorie } from '../../composables/prolongatie-historie';

const huidigJaar = new Date().getFullYear();

const jaar = defineModel<number | undefined>('jaar');
const pakket = defineModel<string | undefined>('pakket');

const getPakketten = usePakketten({ enabled: true });

const getProlongatieHistorie = useProlongatieHistorie(pakket, jaar, {
  enabled: () => !!pakket.value,
});

useWatchErrorHandler(getPakketten.error);
useWatchErrorHandler(getProlongatieHistorie.error);

const polisJaartallen = computed(() => {
  const laagsteIngangsjaar = Math.min(
    ...(getPakketten.data.value?.pakketten.map((p) => p.ingangsJaar) ?? []),
  );

  return Array.from(
    { length: huidigJaar - laagsteIngangsjaar + 1 },
    (_, i) => laagsteIngangsjaar + i,
  );
});

watch(
  getPakketten.data,
  (newValue) => {
    if ((newValue?.pakketten ?? []).length > 1) {
      return;
    }

    pakket.value = newValue?.pakketten[0].pakketPolisnummer;
  },
  { immediate: true },
);

watch(
  [polisJaartallen, pakket],
  ([newPolisJaartallen, newPakket], [, oldPakket]) => {
    if (!newPakket && !oldPakket) {
      return;
    }

    jaar.value = newPolisJaartallen.at(-1);
  },
  { immediate: true },
);
</script>

<template>
  <form class="flex flex-wrap justify-end gap-x-4 gap-y-2 self-baseline">
    <FloatLabel v-if="(getPakketten.data.value?.pakketten ?? []).length > 1" class="grow-0">
      <VSelect
        v-model="pakket"
        class="basis-40"
        id="prolongatiewijzigingen-pakketten"
        :loading="getPakketten.isPending.value"
        option-label="pakketPolisnummer"
        option-value="pakketPolisnummer"
        :options="getPakketten.data.value?.pakketten"
        show-clear
        size="sm"
      />

      <label>Filter op pakket</label>
    </FloatLabel>

    <ExpandTransition direction="horizontal">
      <FloatLabel v-show="getProlongatieHistorie.isEnabled.value" class="grow-0">
        <VSelect
          v-model="jaar"
          class="basis-40"
          :loading="getProlongatieHistorie.isPending.value"
          :options="polisJaartallen"
          size="sm"
        />

        <label>Filter op jaar</label>
      </FloatLabel>
    </ExpandTransition>
  </form>
</template>
